import React, { useCallback, useEffect, useState } from 'react';
import T from './theme';

// My foods: the athlete's remembered estimates (single foods, per unit) and
// saved recipes. Confirming or correcting an estimate makes it theirs
// ("mine"): later logs use their numbers and stop flagging it. Forgetting one
// means the next log asks Grok again. Everything here is per user; the API
// never returns another athlete's rows.

export const MACRO_FIELDS = [
  ['kcal', 'kcal'],
  ['pro_g', 'protein g'],
  ['cho_g', 'carbs g'],
  ['fat_g', 'fat g'],
  ['na_mg', 'sodium mg'],
  ['fiber_g', 'fiber g'],
];

// Unconfirmed first, then the ones you eat most: the estimates worth
// checking are the ones that move your week.
export function sortEstimates(rows) {
  return [...(rows || [])].sort((a, b) => {
    if (Boolean(a.confirmed) !== Boolean(b.confirmed)) return a.confirmed ? 1 : -1;
    if ((b.uses || 0) !== (a.uses || 0)) return (b.uses || 0) - (a.uses || 0);
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

export function macroInvalid(value) {
  if (value === '' || value === null || value === undefined) return false;
  const n = Number(value);
  return !Number.isFinite(n) || n < 0 || n > 5000;
}

function fmt(n, digits = 0) {
  const v = Number(n);
  return Number.isFinite(v) ? v.toFixed(digits) : '–';
}

function MacroLine({ macros }) {
  const m = macros || {};
  return (
    <span style={{ color: T.color.textMuted }}>
      {fmt(m.kcal)} kcal · {fmt(m.pro_g, 1)} P · {fmt(m.cho_g, 1)} C · {fmt(m.fat_g, 1)} F
    </span>
  );
}

function EstimateRow({ est, api, onChanged, setError }) {
  const [editing, setEditing] = useState(false);
  const [confirmForget, setConfirmForget] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    const m = est.macros || {};
    setDraft({
      portion_label: est.portion_label || '',
      ...Object.fromEntries(MACRO_FIELDS.map(([k]) => [k, m[k] ?? ''])),
    });
    setEditing(true);
  };

  const save = async (macros, portionLabel) => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/nutrition/estimates/${est.id}`, { macros, portion_label: portionLabel });
      setEditing(false);
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save that food');
    } finally {
      setBusy(false);
    }
  };

  const forget = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/nutrition/estimates/${est.id}`);
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not forget that food');
      setBusy(false);
    }
  };

  const invalid = editing && MACRO_FIELDS.some(([k]) => macroInvalid(draft?.[k]));

  return (
    <li style={{ padding: '8px 0', borderBottom: `1px solid ${T.color.border}` }} data-testid={`estimate-${est.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <strong>{est.name}</strong>
        <span style={{ fontSize: T.font.xs }}>
          {est.confirmed ? (
            <span aria-label="confirmed">✓ mine</span>
          ) : (
            <span aria-label="estimate" style={{ color: T.color.danger }}>⚠ estimate</span>
          )}
          {' · '}used {est.uses || 0}×
        </span>
      </div>
      <div style={{ fontSize: T.font.sm }}>
        per {est.portion_label || est.unit || 'serving'} · <MacroLine macros={est.macros} />
      </div>

      {editing ? (
        <div style={{ marginTop: 6 }}>
          <label style={{ display: 'block', fontSize: T.font.sm, marginBottom: 4 }}>
            Portion
            <input
              value={draft.portion_label}
              onChange={(e) => setDraft({ ...draft, portion_label: e.target.value })}
              aria-label={`portion for ${est.name}`}
              style={{ display: 'block', width: 160 }}
            />
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {MACRO_FIELDS.map(([k, label]) => (
              <label key={k} style={{ fontSize: T.font.xs }}>
                {label}
                <input
                  inputMode="decimal"
                  value={draft[k]}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  aria-label={`${label} for ${est.name}`}
                  aria-invalid={macroInvalid(draft[k])}
                  style={{ display: 'block', width: 72 }}
                />
              </label>
            ))}
          </div>
          <p style={{ margin: '6px 0 0' }}>
            <button
              type="button"
              disabled={busy || invalid}
              onClick={() => {
                const macros = Object.fromEntries(MACRO_FIELDS.map(([k]) => [k, draft[k] === '' ? 0 : Number(draft[k])]));
                save(macros, draft.portion_label);
              }}
            >
              Save as mine
            </button>{' '}
            <button type="button" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </p>
        </div>
      ) : (
        <p style={{ margin: '6px 0 0', fontSize: T.font.sm }}>
          {!est.confirmed && (
            <>
              <button type="button" disabled={busy} onClick={() => save(est.macros, est.portion_label)}>
                Looks right
              </button>{' '}
            </>
          )}
          <button type="button" disabled={busy} onClick={startEdit}>Edit</button>{' '}
          {confirmForget ? (
            <>
              <button type="button" disabled={busy} onClick={forget} aria-label={`really forget ${est.name}`}>
                Forget it
              </button>{' '}
              <button type="button" disabled={busy} onClick={() => setConfirmForget(false)}>Keep</button>
            </>
          ) : (
            <button type="button" disabled={busy} onClick={() => setConfirmForget(true)}>Forget…</button>
          )}
        </p>
      )}
    </li>
  );
}

function RecipeRow({ recipe, api, onChanged, setError }) {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/nutrition/recipes/${recipe.id}`);
      onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete that recipe');
      setBusy(false);
    }
  };

  const ingredients = recipe.ingredients || [];
  return (
    <li style={{ padding: '8px 0', borderBottom: `1px solid ${T.color.border}` }} data-testid={`recipe-${recipe.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <strong>{recipe.dish}</strong>
        <span style={{ fontSize: T.font.xs, color: T.color.textMuted }}>
          {ingredients.length} ingredient{ingredients.length === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ fontSize: T.font.sm }}>
        per {recipe.serving_label || 'serving'} · <MacroLine macros={recipe.totals} />
      </div>
      {open && (
        <ul style={{ fontSize: T.font.sm, margin: '6px 0 0', paddingLeft: 18 }}>
          {ingredients.map((row, i) => (
            <li key={`${row.name}-${i}`}>
              {row.amount || row.qty} {row.name} — {fmt(row.kcal)} kcal
              {row.badge === 'estimate' || row.estimated ? ' ⚠' : ''}
              {row.badge === 'unmatched' ? ' (unmatched)' : ''}
            </li>
          ))}
        </ul>
      )}
      <p style={{ margin: '6px 0 0', fontSize: T.font.sm }}>
        <button type="button" onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide ingredients' : 'Ingredients'}
        </button>{' '}
        {confirmDelete ? (
          <>
            <button type="button" disabled={busy} onClick={remove} aria-label={`really delete ${recipe.dish}`}>
              Delete it
            </button>{' '}
            <button type="button" disabled={busy} onClick={() => setConfirmDelete(false)}>Keep</button>
          </>
        ) : (
          <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete…</button>
        )}
      </p>
    </li>
  );
}

export default function MyFoods({ api, onClose }) {
  const [estimates, setEstimates] = useState(null);
  const [recipes, setRecipes] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [est, rec] = await Promise.all([
        api.get('/nutrition/estimates'),
        api.get('/nutrition/recipes'),
      ]);
      setEstimates(sortEstimates(est.data?.estimates));
      setRecipes(rec.data?.recipes || []);
    } catch (e) {
      setError('Could not load your foods');
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const unconfirmed = (estimates || []).filter((e) => !e.confirmed).length;

  return (
    <div
      style={{
        border: `1px solid ${T.color.border}`,
        borderLeft: `3px solid ${T.color.accentNutrition}`,
        borderRadius: T.radius.base,
        padding: 12,
        margin: '8px 0',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h4 style={{ margin: '0 0 4px' }}>My foods</h4>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="close my foods"
            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px', color: T.color.textMuted }}
          >
            ×
          </button>
        )}
      </div>
      {error && <p role="alert" style={{ color: T.color.danger, fontSize: T.font.sm }}>{error}</p>}

      <h5 style={{ margin: '8px 0 2px' }}>Remembered foods</h5>
      <p style={{ fontSize: T.font.sm, color: T.color.textMuted, margin: 0 }}>
        Grok estimated these once; UMAX reuses them. {unconfirmed > 0
          ? `${unconfirmed} still ${unconfirmed === 1 ? 'is an estimate' : 'are estimates'} — fix or confirm the ones you eat often.`
          : 'All confirmed.'}
      </p>
      {estimates === null ? (
        <p style={{ fontSize: T.font.sm }}>Loading…</p>
      ) : estimates.length === 0 ? (
        <p style={{ fontSize: T.font.sm }}>Nothing remembered yet. Log a meal and UMAX will learn.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {estimates.map((est) => (
            <EstimateRow key={est.id} est={est} api={api} onChanged={load} setError={setError} />
          ))}
        </ul>
      )}

      <h5 style={{ margin: '12px 0 2px' }}>My recipes</h5>
      {recipes === null ? (
        <p style={{ fontSize: T.font.sm }}>Loading…</p>
      ) : recipes.length === 0 ? (
        <p style={{ fontSize: T.font.sm }}>No saved recipes yet. Use “Break down a dish” and accept one.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {recipes.map((r) => (
            <RecipeRow key={r.id} recipe={r} api={api} onChanged={load} setError={setError} />
          ))}
        </ul>
      )}
      {onClose && (
        <p style={{ margin: '10px 0 0', textAlign: 'right' }}>
          <button type="button" onClick={onClose}>Close my foods</button>
        </p>
      )}
    </div>
  );
}
